/**
 * Dependency graph builder and topological sort for the proof. evaluator (Track 2.B / P3.GRAMMAR).
 *
 * Spec: extensibility-design.md §5.3, §3.4.0 (case-folding), §10 (Map.get invariant).
 *
 * Responsibilities:
 * - Extract all variable declarations from the parsed AST.
 * - Walk every expression to discover which variables each variable reads.
 * - Detect cycles at graph-build time (returns error entries for cycle members).
 * - Produce a topological sort (evaluation order).
 * - Handle @bind bidirectional edges — the pivot determines directionality.
 * - Handle scaling-rule graph as a second layer.
 *
 * Case-folding (extensibility-design.md §3.4.0 / L13):
 *   Variable keys are canonical lowercase: `makeVarKey` lowercases the label.
 *   The `displayName` map on `DependencyGraph` stores first-occurrence casing.
 *
 * Map.get invariant (extensibility-design.md §10 / P1.B):
 *   All variable-keyed lookups go through `Map.get(canonicalKey)`.
 *   No plain-object property access by user-derived string.
 *
 * Variable key format: `scopePath|sectionKeyword|lowercaseLabel`
 * e.g. `biga|ingredients|flour` or `|ingredients|water` (root scope).
 *
 * Pure TS — no DOM, no Node built-ins.
 */

import type {
  DocumentNode,
  TopLevelNode,
  SectionBlockNode,
  LabelledLineNode,
  BindingNode,
  DefinitionNode,
  ExpressionNode,
  ScopePath,
} from "../ast/types.js";
import { CONTENT_INLINE_KEYWORDS } from "../parser/index.js";

// ---------------------------------------------------------------------------
// Variable key helpers
// ---------------------------------------------------------------------------

/**
 * Canonical case fold for a user-supplied label (extensibility-design.md §3.4.0).
 *
 * THE single source of truth for label folding. Every lookup that takes a
 * user-authored label (labelIndex, varKey construction, reference resolution)
 * MUST route through this helper — re-implementing the fold is the bug class
 * behind QA 2026-06-11 A4 (capitalised recipes resolving nothing).
 */
export function foldLabel(label: string): string {
  return label.toLowerCase();
}

/**
 * Canonical case fold for a scope path: every segment lowercased, joined
 * with `/`. Root scope (empty path) folds to the empty string.
 *
 * Companion to {@link foldLabel} — same single-source-of-truth contract.
 */
export function foldScopePath(scopePath: ScopePath): string {
  return scopePath.map((s) => s.toLowerCase()).join("/");
}

/**
 * Unique key for a variable across the whole recipe.
 *
 * Format: `<scope>|<section>|<canonicalLabel>`
 * Root scope uses the empty string for scope.
 *
 * Case-folding (extensibility-design.md §3.4.0): the label is lowercased
 * so that `Tipo 00 Flour` and `tipo 00 flour` map to the same key.
 * The `displayName` map on `DependencyGraph` stores the first-occurrence casing.
 *
 * Scope path segments are also case-folded for key lookup.
 */
export function makeVarKey(scopePath: ScopePath, sectionKeyword: string, label: string): string {
  return `${foldScopePath(scopePath)}|${sectionKeyword}|${foldLabel(label)}`;
}

/**
 * Node-level convenience over {@link makeVarKey}: build the canonical varKey
 * for any AST node carrying `scopePath` + `sectionKeyword` + `label`
 * (e.g. a LabelledLineNode).
 *
 * UI code MUST use this instead of assembling the `a|b|c` key shape by hand —
 * the apps/web ESLint guard bans the ad-hoc template (QA 2026-06-11 A4).
 */
export function varKeyForNode(node: {
  scopePath: ScopePath;
  sectionKeyword: string;
  label: string;
}): string {
  return makeVarKey(node.scopePath, node.sectionKeyword, node.label);
}

/** Parse a varKey back to its parts. */
export function parseVarKey(key: string): { scope: string; section: string; label: string } {
  const parts = key.split("|");
  return {
    scope: parts[0] ?? "",
    section: parts[1] ?? "",
    label: parts[2] ?? "",
  };
}

// ---------------------------------------------------------------------------
// Variable declaration
// ---------------------------------------------------------------------------

export type VarKind =
  | "literal" // plain numeric value (no expression)
  | "expression" // value slot starts with =
  | "binding" // @bind virtual variable
  | "bind-pivot" // the pivot variable of a @bind (receives the inverse expression)
  | "definition"; // @define formula (callable, not a value itself)

export interface VarDeclaration {
  key: string;
  kind: VarKind;
  label: string;
  scopePath: ScopePath;
  sectionKeyword: string;
  /** Raw value string for literals. */
  rawValue?: string;
  /** Parsed expression, if expression or binding kind. */
  expression?: ExpressionNode;
  /**
   * For an incomplete explicit `=expr` (e.g. trailing operator `=water +`): the
   * evaluator resolves this key to a `grammar-invalid` error carrying this
   * message instead of a partial value (P3.EDITOR item 5).
   */
  exprError?: string;
  /** For binding kind: the formula expression (variable = expr). */
  bindingExpr?: ExpressionNode;
  /** For binding kind: the pivot variable name. */
  pivot?: string;
  /** For bind-pivot: the inverse expression (pivot = f^{-1}(virtual)). */
  inverseExpr?: ExpressionNode;
  /** For definition kind: parameter names. */
  params?: readonly string[];
  /** For scaling-rule: the scaling coefficient or expression. */
  scalingCoefficient?: number;
  scalingExpression?: ExpressionNode;
  /** Whether this variable participates in scale operations. */
  scalable: boolean;
  /** Source line for error reporting. */
  sourceLine?: number;
}

// ---------------------------------------------------------------------------
// Graph node (one entry per variable key)
// ---------------------------------------------------------------------------

export interface GraphNode {
  key: string;
  decl: VarDeclaration;
  /** Variable keys this node depends on (reads). */
  deps: Set<string>;
  /** Variable keys that depend on this node (reverse edges). */
  dependents: Set<string>;
  /** True if this node is part of a detected cycle. */
  inCycle: boolean;
}

// ---------------------------------------------------------------------------
// Dependency graph
// ---------------------------------------------------------------------------

export interface DependencyGraph {
  /** All nodes, keyed by variable key (canonical lowercase). */
  nodes: Map<string, GraphNode>;
  /** Topological sort of non-cycle nodes (evaluation order). */
  topoOrder: string[];
  /** Keys of variables participating in cycles. */
  cycleKeys: Set<string>;
  /**
   * Lookup map: canonical-lowercase label → possible keys.
   * Used for reference resolution in expressions.
   * All access via Map.get() — never plain object key access.
   */
  labelIndex: Map<string, string[]>;
  /**
   * Display-name map: variable key → first-occurrence display name.
   *
   * Preserves the casing typed by the user (e.g. `"Tipo 00 Flour"`) for use
   * in the Values panel, inline chips, and error messages, while the graph
   * key is always lowercase canonical.
   *
   * extensibility-design.md §3.4.0 — "display preserves original case".
   * Convergence rule: the first occurrence in document order wins.
   */
  displayName: Map<string, string>;
}

// ---------------------------------------------------------------------------
// Expression reference extractor
// ---------------------------------------------------------------------------

/**
 * Walk an expression and collect all variable references as label strings.
 * We do not resolve scope yet — that happens during evaluation.
 */
function collectRefs(expr: ExpressionNode, out: Set<string>): void {
  switch (expr.kind) {
    case "literal":
      break;
    case "reference":
      // path may be ["flour"] or ["Biga", "flour"] or ["@ingredients", "flour"]
      // We collect the last segment as the label (or the full path joined)
      out.add(expr.path.join("/"));
      break;
    case "binary-op":
      collectRefs(expr.left, out);
      collectRefs(expr.right, out);
      break;
    case "unary-minus":
      collectRefs(expr.operand, out);
      break;
    case "function-call":
      for (const arg of expr.args) collectRefs(arg, out);
      break;
    case "aggregation":
      // Aggregations reference a section — we record the target as a ref
      out.add(expr.target);
      break;
  }
}

// ---------------------------------------------------------------------------
// Scalar traits inference (from section keyword)
// ---------------------------------------------------------------------------

/**
 * Section keywords whose value rows the evaluator treats as scalable under an
 * active `@scale` directive. The single source of truth — consumers that need a
 * UI-side scalable gate (e.g. the Cook scale slider) MUST import
 * `isScalableSection` from `@proof/grammar` rather than re-declaring this list.
 */
export const SCALABLE_SECTIONS: ReadonlySet<string> = new Set([
  "ingredients",
  "marinade",
  "brine",
  "sauce",
  "glaze",
  "dough",
  "filling",
]);

/** True when `keyword` names a scalable section (case-insensitive). */
export function isScalableSection(keyword: string): boolean {
  return SCALABLE_SECTIONS.has(keyword.toLowerCase());
}

// ---------------------------------------------------------------------------
// @bind inverse expression builder — superseded
// ---------------------------------------------------------------------------

// The buildInverseExpr stub that was here (returning undefined for all inputs)
// has been removed. Symbolic inversion is now in packages/grammar/src/evaluator/inverter.ts
// (analyzeSolvability + buildInverseExpr from the back-solve-v1 plan).
// The graph-builder no longer pre-builds inverse expressions; the inverter is
// called on demand by the back-solve worker handler.

// ---------------------------------------------------------------------------
// AST walker — collect VarDeclarations
// ---------------------------------------------------------------------------

function collectDeclarations(doc: DocumentNode): VarDeclaration[] {
  const decls: VarDeclaration[] = [];

  for (const node of doc.children) {
    walkTopLevel(node, decls);
  }

  return decls;
}

function walkTopLevel(node: TopLevelNode, decls: VarDeclaration[]): void {
  switch (node.kind) {
    case "section-block":
      walkSection(node, decls);
      break;
    case "binding":
      walkBinding(node, decls);
      break;
    case "definition":
      walkDefinition(node, decls);
      break;
    case "scale-directive":
      // Scale directives are handled in the scaling layer, not the value graph.
      break;
    default:
      break;
  }
}

function walkSection(block: SectionBlockNode, decls: VarDeclaration[]): void {
  // Content-inline keywords (@intent, @plan-next, @todo) are inert annotations:
  // their body is free prose, never a value binding. A labelled-line-shaped line
  // inside one (e.g. `@intent` then `hydration: 80`) must NOT become a Values-panel
  // variable — that would break the inert-annotation contract the grammar docs
  // state (Track A decision A1). Bindings/definitions authored under such a block
  // are still collected below (they are explicit @bind/@define directives, not the
  // block's prose content), so nested @bind semantics are unaffected.
  const inertContent = CONTENT_INLINE_KEYWORDS.has(block.keyword);
  for (const child of block.children) {
    // Track 2.A grammar-construct directives can nest inside a section body when
    // authored mid-section (they do NOT open a section). Collect their
    // declarations here so a @bind / @define written under `@ingredients`
    // evaluates identically to one written at the top level. Their varKeys are
    // still keyed under "@bind" / "@define" (see walkBinding / walkDefinition),
    // independent of the enclosing section.
    if (child.kind === "binding") {
      walkBinding(child, decls);
      continue;
    }
    if (child.kind === "definition") {
      walkDefinition(child, decls);
      continue;
    }
    if (child.kind !== "labelled-line") continue;
    if (inertContent) continue;
    walkLabelledLine(child, block.keyword, decls);
  }
}

function walkLabelledLine(
  line: LabelledLineNode,
  sectionKeyword: string,
  decls: VarDeclaration[],
): void {
  const key = makeVarKey(line.scopePath, sectionKeyword, line.label);
  const sourceLine = line.span?.start.line;
  const scalingCoefficient = line.scalingRule?.coefficient;
  const scalingExpression = line.scalingRule?.expression;

  if (line.exprError !== undefined) {
    // Incomplete explicit formula (`=water +`): an expression-kind decl with no
    // `expression` and an `exprError`. The pipeline resolves it to a
    // `grammar-invalid` error value → the editor renders `→?` (P3.EDITOR item 5).
    decls.push({
      key,
      kind: "expression",
      label: line.label,
      scopePath: line.scopePath,
      sectionKeyword,
      exprError: line.exprError,
      scalable: isScalableSection(sectionKeyword),
      ...(scalingCoefficient !== undefined ? { scalingCoefficient } : {}),
      ...(scalingExpression !== undefined ? { scalingExpression } : {}),
      ...(sourceLine !== undefined ? { sourceLine } : {}),
    });
  } else if (line.expression !== undefined) {
    decls.push({
      key,
      kind: "expression",
      label: line.label,
      scopePath: line.scopePath,
      sectionKeyword,
      expression: line.expression,
      scalable: isScalableSection(sectionKeyword),
      ...(scalingCoefficient !== undefined ? { scalingCoefficient } : {}),
      ...(scalingExpression !== undefined ? { scalingExpression } : {}),
      ...(sourceLine !== undefined ? { sourceLine } : {}),
    });
  } else {
    decls.push({
      key,
      kind: "literal",
      label: line.label,
      scopePath: line.scopePath,
      sectionKeyword,
      rawValue: line.rawValue,
      scalable: isScalableSection(sectionKeyword),
      ...(scalingCoefficient !== undefined ? { scalingCoefficient } : {}),
      ...(scalingExpression !== undefined ? { scalingExpression } : {}),
      ...(sourceLine !== undefined ? { sourceLine } : {}),
    });
  }
}

function walkBinding(node: BindingNode, decls: VarDeclaration[]): void {
  const key = makeVarKey(node.scopePath, "@bind", node.variableName);
  const sourceLine = node.span?.start.line;

  // Virtual variable (the @bind name)
  decls.push({
    key,
    kind: "binding",
    label: node.variableName,
    scopePath: node.scopePath,
    sectionKeyword: "@bind",
    bindingExpr: node.expression,
    pivot: node.pivot,
    scalable: false,
    ...(sourceLine !== undefined ? { sourceLine } : {}),
  });

  // Add a bind-pivot marker so the evaluator knows this variable can be
  // recomputed from the binding. Inverse expressions are built on demand by
  // the back-solve worker handler (inverter.ts) rather than pre-built here.
  const pivotKey = makeVarKey(node.scopePath, "@bind-pivot", node.pivot);
  decls.push({
    key: pivotKey,
    kind: "bind-pivot",
    label: node.pivot,
    scopePath: node.scopePath,
    sectionKeyword: "@bind-pivot",
    bindingExpr: node.expression,
    pivot: node.variableName, // the virtual var that drives this pivot
    scalable: false,
    ...(sourceLine !== undefined ? { sourceLine } : {}),
  });
}

function walkDefinition(node: DefinitionNode, decls: VarDeclaration[]): void {
  const key = makeVarKey(node.scopePath, "@define", node.name);
  const sourceLine = node.span?.start.line;
  decls.push({
    key,
    kind: "definition",
    label: node.name,
    scopePath: node.scopePath,
    sectionKeyword: "@define",
    expression: node.body,
    params: node.params,
    scalable: false,
    ...(sourceLine !== undefined ? { sourceLine } : {}),
  });
}

// ---------------------------------------------------------------------------
// Label index (canonical-lowercase label → [possible var keys])
// ---------------------------------------------------------------------------

/**
 * Build a label-to-keys index for reference resolution.
 *
 * All keys in this map are canonical lowercase (matching the keys produced
 * by `makeVarKey`). Access is always via Map.get() — never plain object
 * property access.
 *
 * `bind-pivot` declarations are intentionally excluded from the labelIndex.
 * They are internal plumbing (a marker so the evaluator knows which variable
 * is the pivot for a binding) and must not shadow the real user-declared
 * literal/expression variable of the same label. If included, references to
 * a pivot variable (e.g. `water`) would resolve to the `@bind-pivot` marker
 * rather than the `@ingredients` literal, producing undefined-reference errors
 * when `evaluateBindings` tries to resolve the forward expression.
 *
 * extensibility-design.md §10 (Map.get invariant) + §3.4.0 (case-folding).
 */
function buildLabelIndex(decls: VarDeclaration[]): Map<string, string[]> {
  const idx = new Map<string, string[]>();
  for (const decl of decls) {
    // Exclude bind-pivot entries — they are internal markers, not resolvable references.
    if (decl.kind === "bind-pivot") continue;
    // Canonical lowercase key — case-insensitive lookup (foldLabel = §3.4.0).
    const canonicalLabel = foldLabel(decl.label);
    const existing = idx.get(canonicalLabel) ?? [];
    existing.push(decl.key);
    idx.set(canonicalLabel, existing);
  }
  return idx;
}

// ---------------------------------------------------------------------------
// Display-name map (var key → first-occurrence display name)
// ---------------------------------------------------------------------------

/**
 * Build a map from variable key → display name (first-occurrence casing).
 *
 * extensibility-design.md §3.4.0:
 *   "display preserves original case … first occurrence in document order"
 *
 * The convergence rule: two CRDT clients that produce `Tipo 00 Flour` and
 * `tipo 00 flour` converge to the same graph node (same canonical key).
 * The displayName retains whichever casing was observed first in document
 * order. Here, "first" means the first entry in `decls` (which reflects
 * document order from the AST walker).
 */
function buildDisplayNameMap(decls: VarDeclaration[]): Map<string, string> {
  const displayName = new Map<string, string>();
  for (const decl of decls) {
    // Only register the first occurrence (document order → first decl wins).
    if (!displayName.has(decl.key)) {
      displayName.set(decl.key, decl.label);
    }
  }
  return displayName;
}

// ---------------------------------------------------------------------------
// Dependency edge builder
// ---------------------------------------------------------------------------

/**
 * Resolve a reference path to zero or more variable keys.
 *
 * Reference forms:
 *   ["flour"]                 → bare label lookup in label index
 *   ["Biga", "flour"]         → scope-qualified lookup
 *   ["@ingredients", "flour"] → section-qualified lookup
 *   ["../", "flour"]          → parent scope (simplified: same label in parent)
 *   ["@ingredients"]          → entire section reference (aggregation target)
 */
function resolveRefToKeys(
  path: readonly string[],
  callerScope: ScopePath,
  labelIndex: Map<string, string[]>,
  nodeMap: Map<string, GraphNode>,
): string[] {
  if (path.length === 0) return [];

  const first = path[0] ?? "";

  // Keys in nodeMap/labelIndex are canonical (makeVarKey): every user-supplied
  // scope segment / label must be folded before comparison (§3.4.0, QA A4).

  // @section reference (aggregation target or section.field)
  if (first.startsWith("@")) {
    const sectionKw = first.slice(1);
    if (path.length === 1) {
      // whole-section reference — return all variables in that section
      const callerScopeStr = foldScopePath(callerScope);
      const matches: string[] = [];
      for (const key of nodeMap.keys()) {
        const { section, scope } = parseVarKey(key);
        if (section === sectionKw && scope === callerScopeStr) {
          matches.push(key);
        }
      }
      return matches;
    } else {
      // @section.field — look up specific label in that section
      const label = path[1] ?? "";
      const key = makeVarKey(callerScope, sectionKw, label);
      return nodeMap.has(key) ? [key] : [];
    }
  }

  // ../variable — parent scope
  if (first === "../") {
    const label = foldLabel(path[1] ?? "");
    const parentScopeStr = foldScopePath(callerScope.slice(0, -1));
    const matches: string[] = [];
    for (const key of nodeMap.keys()) {
      const { scope, label: keyLabel } = parseVarKey(key);
      if (scope === parentScopeStr && keyLabel === label) matches.push(key);
    }
    return matches;
  }

  // Scope-qualified: ["Biga", "flour"]
  if (path.length >= 2 && !first.startsWith("@")) {
    const scopeStr = foldScopePath(path.slice(0, -1));
    const label = foldLabel(path[path.length - 1] ?? "");
    const matches: string[] = [];
    for (const key of nodeMap.keys()) {
      const { scope, label: keyLabel } = parseVarKey(key);
      if (scope === scopeStr && keyLabel === label) matches.push(key);
    }
    if (matches.length > 0) return matches;
  }

  // Bare label — look up in label index, prefer same-scope
  // Use canonical lowercase for the lookup (Map.get invariant — §10).
  const label = foldLabel(path[path.length - 1] ?? "");
  const candidates = labelIndex.get(label) ?? [];
  if (candidates.length === 0) return [];

  const callerScopeStr = foldScopePath(callerScope);

  // Prefer same-scope candidates
  const sameScope = candidates.filter((k) => {
    const { scope } = parseVarKey(k);
    return scope === callerScopeStr;
  });
  if (sameScope.length > 0) return sameScope;

  // Fall back to all candidates (cross-scope reference)
  return candidates;
}

/**
 * Resolve a reference path to its single canonical varKey, scope-aware.
 *
 * Public wrapper over the internal `resolveRefToKeys` so the back-solve inverter
 * can canonicalise off-path references with the SAME same-scope-preferred logic
 * the forward evaluator (`pipeline.ts` `resolvePathToKeys`) and the graph
 * dependency-edge builder use. This is the single source of truth for reference
 * resolution — re-implementing the fold/scan is the bug class behind the
 * cross-scope label collision (back-solve returned a confident wrong value).
 *
 * Returns the first canonical varKey (same-scope candidate preferred; falls back
 * to the cross-scope candidate), or `null` if nothing resolves.
 */
export function resolveRefPathToKey(
  path: readonly string[],
  callerScope: ScopePath,
  graph: DependencyGraph,
): string | null {
  const keys = resolveRefToKeys(path, callerScope, graph.labelIndex, graph.nodes);
  return keys[0] ?? null;
}

function buildDepsFromExpr(
  expr: ExpressionNode,
  callerScope: ScopePath,
  labelIndex: Map<string, string[]>,
  nodeMap: Map<string, GraphNode>,
): Set<string> {
  const rawRefs = new Set<string>();
  collectRefs(expr, rawRefs);

  const deps = new Set<string>();
  for (const ref of rawRefs) {
    const path = ref.split("/");
    const resolved = resolveRefToKeys(path, callerScope, labelIndex, nodeMap);
    for (const k of resolved) deps.add(k);
  }
  return deps;
}

// ---------------------------------------------------------------------------
// Cycle detection (DFS with colour marking)
// ---------------------------------------------------------------------------

type Colour = "white" | "grey" | "black";

function detectCycles(nodes: Map<string, GraphNode>): Set<string> {
  const colour = new Map<string, Colour>();
  const cycleKeys = new Set<string>();

  for (const key of nodes.keys()) {
    colour.set(key, "white");
  }

  // Iterative DFS with an explicit stack — depth-safe against deeply-chained
  // dependency graphs that would overflow a recursive walk's call stack (closed
  // sandbox: a pathological recipe must not crash the Worker).
  //
  // Each stack frame carries the node key plus the index of the next dependency
  // to visit, mirroring the recursive enter/exit. `path` is the grey stack of
  // keys on the current DFS spine, used to recover the cycle members on a back
  // edge — exactly the same logic as the recursive `path.indexOf(key)`.
  interface Frame {
    key: string;
    deps: string[];
    next: number;
  }

  for (const start of nodes.keys()) {
    if (colour.get(start) !== "white") continue;

    const path: string[] = [];
    const node0 = nodes.get(start);
    colour.set(start, "grey");
    path.push(start);
    const stack: Frame[] = [
      { key: start, deps: node0 !== undefined ? [...node0.deps] : [], next: 0 },
    ];

    while (stack.length > 0) {
      const frame = stack[stack.length - 1]!;

      if (frame.next >= frame.deps.length) {
        // Exhausted this node's dependencies — colour it black and pop.
        colour.set(frame.key, "black");
        path.pop();
        stack.pop();
        continue;
      }

      const dep = frame.deps[frame.next]!;
      frame.next++;

      const c = colour.get(dep);
      if (c === "black") {
        // Already fully explored — no cycle through here.
        continue;
      }
      if (c === "grey") {
        // Back edge — everything on the current spine from `dep` is a cycle.
        const cycleStart = path.indexOf(dep);
        if (cycleStart >= 0) {
          for (const k of path.slice(cycleStart)) cycleKeys.add(k);
        }
        cycleKeys.add(dep);
        continue;
      }

      // White — descend into it.
      colour.set(dep, "grey");
      path.push(dep);
      const depNode = nodes.get(dep);
      stack.push({
        key: dep,
        deps: depNode !== undefined ? [...depNode.deps] : [],
        next: 0,
      });
    }
  }

  return cycleKeys;
}

// ---------------------------------------------------------------------------
// Topological sort (Kahn's algorithm — ignores cycle nodes)
// ---------------------------------------------------------------------------

function topoSort(nodes: Map<string, GraphNode>, cycleKeys: Set<string>): string[] {
  // Build in-degree count, excluding cycle nodes
  const inDegree = new Map<string, number>();
  for (const [key, node] of nodes) {
    if (cycleKeys.has(key)) continue;
    let degree = 0;
    for (const dep of node.deps) {
      if (!cycleKeys.has(dep)) degree++;
    }
    inDegree.set(key, degree);
  }

  // Queue of nodes with zero in-degree
  const queue: string[] = [];
  for (const [key, deg] of inDegree) {
    if (deg === 0) queue.push(key);
  }

  const order: string[] = [];
  while (queue.length > 0) {
    const key = queue.shift()!;
    order.push(key);
    const node = nodes.get(key);
    if (node === undefined) continue;
    for (const dep of node.dependents) {
      if (cycleKeys.has(dep)) continue;
      const current = inDegree.get(dep) ?? 0;
      const next = current - 1;
      inDegree.set(dep, next);
      if (next === 0) queue.push(dep);
    }
  }

  return order;
}

// ---------------------------------------------------------------------------
// Public API: build the dependency graph
// ---------------------------------------------------------------------------

/**
 * Build the full dependency graph from a parsed DocumentNode.
 *
 * Steps:
 * 1. Collect all variable declarations.
 * 2. Build the label index for reference resolution.
 * 3. Build GraphNodes (without edges yet).
 * 4. Fill in dependency edges by walking expressions.
 * 5. Detect cycles.
 * 6. Topological sort.
 */
export function buildDependencyGraph(doc: DocumentNode): DependencyGraph {
  // 1. Declarations
  const decls = collectDeclarations(doc);

  // 2. Label index
  const labelIndex = buildLabelIndex(decls);

  // 3. Build node map
  const nodes = new Map<string, GraphNode>();
  for (const decl of decls) {
    nodes.set(decl.key, {
      key: decl.key,
      decl,
      deps: new Set(),
      dependents: new Set(),
      inCycle: false,
    });
  }

  // 4. Fill dependency edges
  for (const [key, node] of nodes) {
    const { decl } = node;
    let exprToWalk: ExpressionNode | undefined;

    switch (decl.kind) {
      case "expression":
        exprToWalk = decl.expression;
        break;
      case "binding":
        exprToWalk = decl.bindingExpr;
        break;
      case "bind-pivot":
        // bind-pivot depends on the virtual variable (the @bind name)
        // And also on the forward expression's other variables
        if (decl.pivot !== undefined) {
          const virtKey = makeVarKey(decl.scopePath, "@bind", decl.pivot);
          if (nodes.has(virtKey)) {
            node.deps.add(virtKey);
            const virtNode = nodes.get(virtKey);
            if (virtNode !== undefined) virtNode.dependents.add(key);
          }
        }
        exprToWalk = decl.bindingExpr;
        break;
      case "definition":
        exprToWalk = decl.expression;
        break;
      case "literal":
        // No expression — no deps
        break;
    }

    if (exprToWalk !== undefined) {
      const deps = buildDepsFromExpr(exprToWalk, decl.scopePath, labelIndex, nodes);
      for (const dep of deps) {
        if (dep === key) continue; // self-dep (cycle, handled below)
        node.deps.add(dep);
        const depNode = nodes.get(dep);
        if (depNode !== undefined) depNode.dependents.add(key);
      }
    }

    // Scaling rule expression deps
    if (decl.scalingExpression !== undefined) {
      const deps = buildDepsFromExpr(decl.scalingExpression, decl.scopePath, labelIndex, nodes);
      for (const dep of deps) {
        if (dep === key) continue;
        node.deps.add(dep);
        const depNode = nodes.get(dep);
        if (depNode !== undefined) depNode.dependents.add(key);
      }
    }
  }

  // 5. Cycle detection
  const cycleKeys = detectCycles(nodes);
  for (const key of cycleKeys) {
    const n = nodes.get(key);
    if (n !== undefined) n.inCycle = true;
  }

  // 6. Topo sort
  const topoOrder = topoSort(nodes, cycleKeys);

  // 7. Display-name map (first-occurrence casing for Values panel + chips)
  const displayName = buildDisplayNameMap(decls);

  return { nodes, topoOrder, cycleKeys, labelIndex, displayName };
}

// ---------------------------------------------------------------------------
// Dependency closure (for cache invalidation)
// ---------------------------------------------------------------------------

/**
 * Return the set of variable keys that are in the dependency closure of
 * a changed variable. That is, all variables that (transitively) depend on it.
 *
 * Used by the memoization layer: when a variable changes, invalidate its closure.
 */
export function getDependencyClosure(graph: DependencyGraph, changedKey: string): Set<string> {
  const closure = new Set<string>();
  const queue = [changedKey];

  while (queue.length > 0) {
    const key = queue.shift()!;
    if (closure.has(key)) continue;
    closure.add(key);
    const node = graph.nodes.get(key);
    if (node === undefined) continue;
    for (const dep of node.dependents) {
      queue.push(dep);
    }
  }

  return closure;
}
